/**
 * SeoHistory — Ranking-Verlauf aus SEMrush-Snapshots:
 * Chart (Ø Position, Top-10) + Keyword-Positions-Tabelle ueber die Snapshots.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { History, TrendingUp, TrendingDown, Minus, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => new Date(iso).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "2-digit" });

function Trend({ value }) {
  if (value > 0) return <span className="flex items-center gap-0.5 text-emerald-600"><TrendingUp className="w-3 h-3" />+{value}</span>;
  if (value < 0) return <span className="flex items-center gap-0.5 text-red-600"><TrendingDown className="w-3 h-3" />{value}</span>;
  return <Minus className="w-3 h-3 text-slate-300" />;
}

export default function SeoHistory({ companyId, refreshKey, isAdmin }) {
  const [history, setHistory] = useState(null);
  const [kwHistory, setKwHistory] = useState(null);

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const [h, k] = await Promise.all([
        axios.get(`${API}/seo/${companyId}/history`, { withCredentials: true }),
        axios.get(`${API}/seo/${companyId}/history/keywords`, { withCredentials: true }),
      ]);
      setHistory(h.data.snapshots || []);
      setKwHistory(k.data);
    } catch { /* silent */ }
  }, [companyId]);

  useEffect(() => { setHistory(null); setKwHistory(null); load(); }, [companyId, refreshKey, load]);

  const removeSnap = async (id) => {
    if (!window.confirm("Diesen Snapshot endgültig löschen?")) return;
    try {
      await axios.delete(`${API}/seo/${companyId}/history/${id}`, { withCredentials: true });
      toast.success("Snapshot gelöscht");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen"); }
  };

  if (!history || history.length === 0) return null;

  const chartData = history.map((s) => ({
    date: fmtDate(s.created_at),
    "Ø Position": s.avg_position,
    "Top 10": s.top10,
    "Org. Traffic": s.organic_traffic,
  }));
  const hasTraffic = history.some((s) => s.organic_traffic != null);
  const snaps = kwHistory?.snapshots || [];

  return (
    <Card data-testid="seo-history-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <History className="w-4 h-4 text-[#2563EB]" /> Ranking-Verlauf
          <Badge variant="outline" className="text-[10px]">{history.length} Snapshots</Badge>
        </CardTitle>
        <p className="text-xs text-slate-500">
          Jeder SEMrush-Abruf/Import wird automatisch als Snapshot gespeichert — so siehst du die Entwicklung über Monate.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {history.length >= 2 ? (
          <div className="h-56" data-testid="seo-history-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="pos" reversed tick={{ fontSize: 10 }} allowDecimals={false}
                  label={{ value: "Position (besser ↑)", angle: -90, fontSize: 10, position: "insideLeft" }} />
                <YAxis yAxisId="cnt" orientation="right" tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line yAxisId="pos" type="monotone" dataKey="Ø Position" stroke="#2563EB" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                <Line yAxisId="cnt" type="monotone" dataKey="Top 10" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                {hasTraffic && <Line yAxisId="cnt" type="monotone" dataKey="Org. Traffic" stroke="#f59e0b" strokeWidth={1.5} dot={{ r: 2 }} connectNulls />}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-xs text-slate-500" data-testid="seo-history-single-hint">
            Erst 1 Snapshot vorhanden — ab dem zweiten Abruf/Import erscheint hier der Verlaufs-Chart.
          </p>
        )}

        {/* Keyword-Positions-Verlauf */}
        {snaps.length > 0 && kwHistory.keywords?.length > 0 && (
          <div className="max-h-80 overflow-auto border border-[var(--studio-border)] rounded-lg" data-testid="seo-history-kw-table">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-[var(--studio-bg)]">
                <tr className="text-left text-slate-500">
                  <th className="p-2">Keyword</th>
                  <th className="p-2">Vol.</th>
                  {snaps.map((s) => <th key={s.id} className="p-2 whitespace-nowrap">{fmtDate(s.created_at)}</th>)}
                  <th className="p-2">Trend</th>
                </tr>
              </thead>
              <tbody>
                {kwHistory.keywords.map((k) => (
                  <tr key={k.keyword} className="border-t border-[var(--studio-border)]">
                    <td className="p-2 font-medium">{k.keyword}</td>
                    <td className="p-2 text-slate-400">{k.volume ?? "—"}</td>
                    {k.positions.map((p, i) => (
                      <td key={i} className={`p-2 ${p && p <= 10 ? "text-emerald-600 font-semibold" : ""}`}>{p ?? "—"}</td>
                    ))}
                    <td className="p-2"><Trend value={k.trend} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Snapshot-Liste */}
        <div className="flex flex-wrap gap-1.5" data-testid="seo-history-snapshots">
          {history.slice().reverse().map((s) => (
            <span key={s.id} className="flex items-center gap-1.5 border border-[var(--studio-border)] rounded-full px-2.5 py-1 text-[11px]">
              {fmtDate(s.created_at)}
              <Badge variant="outline" className="text-[9px]">{s.source === "api" ? "API" : "Manuell"}</Badge>
              <span className="text-slate-400">{s.keyword_count} KW · Ø {s.avg_position ?? "—"}</span>
              {isAdmin && (
                <button onClick={() => removeSnap(s.id)} className="text-red-400 hover:text-red-600" data-testid={`seo-snap-delete-${s.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
